import type { LanguageCode } from './languages'
import type { NavigationGroup, NavigationItem } from './navigation'

export type EditableNavigationItem = NavigationItem & {
  id: string
  enabled: boolean
  order: number
}

const STORAGE_KEY = 'modi_navigation_items_v1'
const listeners = new Set<(items: EditableNavigationItem[]) => void>()

const label = (tr: string, en: string, ru: string) => ({ tr, en, ru })
const localized = (slug: string): Record<LanguageCode, string> => ({
  tr: `/tr/${slug}`,
  en: `/en/${slug}`,
  ru: `/ru/${slug}`,
})

export const defaultNavigationItems: EditableNavigationItem[] = [
  {
    id: 'home',
    label: label('Anasayfa', 'Home', 'Главная'),
    href: { tr: '/tr/', en: '/en/', ru: '/ru/' },
    enabled: true,
    order: 10,
  },
  {
    id: 'breakfast',
    label: label('Kahvalti', 'Breakfast', 'Завтраки'),
    href: localized('kahvaltilar/'),
    enabled: true,
    order: 20,
  },
  {
    id: 'hot-drinks',
    label: label('Sicak Icecekler', 'Hot Drinks', 'Горячие напитки'),
    href: localized('sicak-icecekler/'),
    enabled: true,
    order: 30,
  },
  {
    id: 'cold-drinks',
    label: label('Soguk Icecekler', 'Cold Drinks', 'Холодные напитки'),
    href: localized('soguk-icecekler/'),
    enabled: true,
    order: 40,
  },
  {
    id: 'desserts',
    label: label('Tatli & Pastane', 'Desserts', 'Десерты'),
    href: localized('tatlilar/'),
    enabled: true,
    order: 50,
  },
  {
    id: 'campaigns',
    label: label('Kampanyalar', 'Campaigns', 'Акции'),
    href: localized('kampanyalar/'),
    enabled: true,
    order: 60,
  },
  {
    id: 'about',
    label: label('Hakkimizda', 'About', 'О нас'),
    href: localized('hakkimizda/'),
    enabled: true,
    order: 70,
  },
  {
    id: 'contact',
    label: label('Iletisim', 'Contact', 'Контакты'),
    href: localized('iletisim/'),
    enabled: true,
    order: 80,
  },
]

export function getNavigationItems() {
  return sortNavigation(readItems())
}

export function saveNavigationItems(items: EditableNavigationItem[]) {
  const sorted = sortNavigation(items)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sorted))
  listeners.forEach((listener) => listener(sorted))
  saveNavigationItemsToFirebase(sorted).catch(() => undefined)
}

export function subscribeNavigationItems(listener: (items: EditableNavigationItem[]) => void) {
  listeners.add(listener)
  listener(getNavigationItems())

  let unsubscribe: (() => void) | undefined

  import('firebase/firestore')
    .then(async (firestoreModule) => {
      const firebaseModule = await import('./firebase')
      unsubscribe = firestoreModule.onSnapshot(
        firestoreModule.doc(firebaseModule.firestore, 'siteSettings', 'navigation'),
        (snapshot) => {
          if (!snapshot.exists()) return
          const data = snapshot.data() as { items?: EditableNavigationItem[] }
          const items = data.items && data.items.length > 0 ? sortNavigation(data.items) : defaultNavigationItems
          localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
          listener(items)
        },
        () => undefined,
      )
    })
    .catch(() => undefined)

  return () => {
    listeners.delete(listener)
    unsubscribe?.()
  }
}

export function toNavigationItems(items: EditableNavigationItem[]): NavigationItem[] {
  return sortNavigation(items)
    .filter((item) => item.enabled)
    .map(({ label, href, children }) => ({ label, href, children }))
}

export function toMobileNavigationGroups(items: EditableNavigationItem[]): NavigationGroup[] {
  const visibleItems = sortNavigation(items).filter((item) => item.enabled)
  const cafeIds = new Set(['home', 'about', 'contact'])
  const cafeItems = visibleItems.filter((item) => cafeIds.has(item.id))
  const menuItems = visibleItems.filter((item) => !cafeIds.has(item.id))

  return [
    { label: label('Modi Cafe', 'Modi Cafe', 'Modi Cafe'), items: toNavigationItems(cafeItems) },
    { label: label('Menu', 'Menu', 'Меню'), items: toNavigationItems(menuItems) },
  ]
}

function readItems(): EditableNavigationItem[] {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return defaultNavigationItems

  try {
    const parsed = JSON.parse(raw) as EditableNavigationItem[]
    return parsed.length > 0 ? parsed : defaultNavigationItems
  } catch {
    return defaultNavigationItems
  }
}

function sortNavigation(items: EditableNavigationItem[]) {
  return items.slice().sort((first, second) => first.order - second.order)
}

async function saveNavigationItemsToFirebase(items: EditableNavigationItem[]) {
  const firestoreModule = await import('firebase/firestore')
  const firebaseModule = await import('./firebase')
  await firebaseModule.ensureFirebaseAuth()
  await firestoreModule.setDoc(
    firestoreModule.doc(firebaseModule.firestore, 'siteSettings', 'navigation'),
    { items },
  )
}
